/*
 * 作物图只有一套：UI-ASSET-006 v1（r7k）五种作物各“生长中 / 成熟”两档，512×512 透明底，字节不改。
 * 图只表达物种与阶段；进度、成熟、收获一律以服务端事实为准。
 * - cropArt()：图 + 把作物“种进地里”的几何。菜园地块、小窝院子的菜园入口（两处都用 GardenScene 的 GardenBed）、
 *   种菜面板缩略图都读它。
 * - cropVisual()：只要图的地方用的旧名字，等同 cropArt(...).src（旧的木框时代作物图已不再引用）。
 * - produceVisual()：摘下来的那一颗用 UI-ASSET-003 的收成小图；表外作物退回 cropArt 的中性图，和地里显示的一致。
 */
const ART = "/ui-assets/UI-ASSET-006/v1";

export const KNOWN_CROPS = ["star_tomato", "sun_pea", "sea_salt_pea", "moon_radish", "sakura_radish"] as const;
export type KnownCrop = (typeof KNOWN_CROPS)[number];
export type CropStage = "growing" | "ripe";

export function isKnownCrop(cropKey: string | null | undefined): cropKey is KnownCrop {
  return typeof cropKey === "string" && (KNOWN_CROPS as readonly string[]).includes(cropKey);
}

/**
 * 一张作物图怎么“种”进地里（数值都从 v1 的透明通道逐行量出，图 512×512）：
 * - width：作物图宽占土块图宽的百分比（地块约 150px 宽时作物约 100–110px，素材按这个尺寸设计）；
 * - anchorX / anchorY：作物与地面的交点在图里的位置（0–1），对准垄沟里的种植点。
 *   番茄、豌豆是茎基，接近图底边中点（番茄生长中底下外露的小根算在土里）；
 *   萝卜成熟是球根最宽处——下半个球根和细根在土里，由前景土层挡住；萝卜生长中是肩部最宽处；
 * - ring：入土处的半宽（占作物图宽）。土面和它相交是一圈，俯视下成一个扁椭圆，前半弧比入土线低——
 *   成熟萝卜在入土线（球根最宽处）以下还能露出前面那一弯，像坐在土窝里，而不是被一刀平切；
 *   茎和生长中萝卜的肩只有很窄一圈，等于平切（萝卜肩下沿是画出来的圆边，要整条埋掉）。
 * - top / left / right：可见内容的包围盒，给成熟光晕和点按范围用。
 * root 标出根茎作物（球根有一截在土里）。
 */
export interface CropArt {
  /** 已知作物的 key；表外作物一律 "unknown"（不把原始代码带进界面）。 */
  key: KnownCrop | "unknown";
  src: string;
  width: number;
  anchorX: number;
  anchorY: number;
  ring: number;
  top: number;
  left: number;
  right: number;
  root: boolean;
}

type Geometry = Omit<CropArt, "key" | "src">;

const GEOMETRY: Record<`${CropStage}:${KnownCrop}`, Geometry> = {
  // 茎基 x≈256、y≈488，往下是外露小根（y 490–505），埋进土里
  "growing:star_tomato": { width: 70, anchorX: 0.501, anchorY: 0.953, ring: 0.017, top: 0.113, left: 0.082, right: 0.916, root: false },
  // 茎基 x 246–272、y≈500；右下两片叶尖垂到图底，一起入土
  "ripe:star_tomato": { width: 74, anchorX: 0.506, anchorY: 0.977, ring: 0.025, top: 0.174, left: 0.082, right: 0.916, root: false },
  // 茎在图中线偏左（x 220–233）
  "growing:sun_pea": { width: 66, anchorX: 0.442, anchorY: 0.977, ring: 0.013, top: 0.084, left: 0.197, right: 0.799, root: false },
  "ripe:sun_pea": { width: 72, anchorX: 0.511, anchorY: 0.977, ring: 0.015, top: 0.109, left: 0.082, right: 0.914, root: false },
  "growing:sea_salt_pea": { width: 66, anchorX: 0.504, anchorY: 0.977, ring: 0.012, top: 0.084, left: 0.119, right: 0.879, root: false },
  "ripe:sea_salt_pea": { width: 72, anchorX: 0.478, anchorY: 0.977, ring: 0.011, top: 0.102, left: 0.084, right: 0.916, root: false },
  // 肩部 y 460–508，最宽在 y≈499（x 169–314）；往下的圆边埋进土里（入土圈取亚像素，等于平切，圆边一点不露）
  "growing:moon_radish": { width: 70, anchorX: 0.472, anchorY: 0.975, ring: 0.004, top: 0.354, left: 0.084, right: 0.916, root: true },
  // 球根 y≈248–452，最宽 y≈344–352（x 126–357，半宽 115）；细根 y 452–510
  "ripe:moon_radish": { width: 64, anchorX: 0.472, anchorY: 0.688, ring: 0.225, top: 0.086, left: 0.186, right: 0.811, root: true },
  "growing:sakura_radish": { width: 70, anchorX: 0.476, anchorY: 0.975, ring: 0.004, top: 0.365, left: 0.084, right: 0.916, root: true },
  // 球根 y≈240–445，最宽 y≈336–346（x 127–344，半宽 108）
  "ripe:sakura_radish": { width: 64, anchorX: 0.46, anchorY: 0.676, ring: 0.212, top: 0.084, left: 0.211, right: 0.787, root: true },
};

/**
 * 表外作物：用月光萝卜“生长中”那丛叶子当中性的生长中图，入土点提到肩部上沿（y≈461），
 * 只露叶子、不露有颜色的肩，不冒充任何一种作物；成熟也用它（光晕照常由代码加）。
 */
const NEUTRAL: CropArt = {
  key: "unknown",
  src: `${ART}/crop-growing-moon_radish.webp`,
  width: 66,
  anchorX: 0.472,
  anchorY: 0.9,
  ring: 0.004,
  top: 0.354,
  left: 0.084,
  right: 0.916,
  root: false,
};

const VISUAL049_GEOMETRY: Record<`${CropStage}:${KnownCrop}`, Geometry> = {
  "growing:moon_radish": {
    "width": 52,
    "anchorX": 0.5,
    "anchorY": 0.85,
    "ring": 0.012,
    "top": 0.1021,
    "left": 0.051,
    "right": 0.9785,
    "root": true
  },
  "growing:sakura_radish": {
    "width": 52,
    "anchorX": 0.5,
    "anchorY": 0.84,
    "ring": 0.012,
    "top": 0.1045,
    "left": 0.0566,
    "right": 0.9577,
    "root": true
  },
  "growing:sea_salt_pea": {
    "width": 52,
    "anchorX": 0.5,
    "anchorY": 0.9,
    "ring": 0.012,
    "top": 0.0694,
    "left": 0.2033,
    "right": 0.8158,
    "root": false
  },
  "growing:star_tomato": {
    "width": 52,
    "anchorX": 0.5,
    "anchorY": 0.895,
    "ring": 0.012,
    "top": 0.0829,
    "left": 0.1427,
    "right": 0.8947,
    "root": false
  },
  "growing:sun_pea": {
    "width": 52,
    "anchorX": 0.5,
    "anchorY": 0.91,
    "ring": 0.012,
    "top": 0.0598,
    "left": 0.2344,
    "right": 0.7935,
    "root": false
  },
  "ripe:moon_radish": {
    "width": 60,
    "anchorX": 0.5,
    "anchorY": 0.85,
    "ring": 0.075,
    "top": 0.0941,
    "left": 0.0152,
    "right": 0.9872,
    "root": true
  },
  "ripe:sakura_radish": {
    "width": 60,
    "anchorX": 0.5,
    "anchorY": 0.855,
    "ring": 0.075,
    "top": 0.1077,
    "left": 0.055,
    "right": 0.9577,
    "root": true
  },
  "ripe:sea_salt_pea": {
    "width": 60,
    "anchorX": 0.5,
    "anchorY": 0.915,
    "ring": 0.012,
    "top": 0.0582,
    "left": 0.2033,
    "right": 0.8405,
    "root": false
  },
  "ripe:star_tomato": {
    "width": 60,
    "anchorX": 0.5,
    "anchorY": 0.895,
    "ring": 0.012,
    "top": 0.0789,
    "left": 0.1603,
    "right": 0.8413,
    "root": false
  },
  "ripe:sun_pea": {
    "width": 60,
    "anchorX": 0.5,
    "anchorY": 0.965,
    "ring": 0.012,
    "top": 0.0048,
    "left": 0.181,
    "right": 0.8182,
    "root": false
  }
};

export function cropArt(cropKey: string | null, stage: CropStage, variant?: "visual049"): CropArt {
  if (variant === "visual049") {
    if (!isKnownCrop(cropKey)) return { key:"unknown", src:"/ui-assets/UI049/v1/crop-growing-moon_radish.png", ...VISUAL049_GEOMETRY["growing:moon_radish"], root:false };
    return { key:cropKey, src:`/ui-assets/UI049/v1/crop-${stage}-${cropKey}.png`, ...VISUAL049_GEOMETRY[`${stage}:${cropKey}`] };
  }
  if (!isKnownCrop(cropKey)) return NEUTRAL;
  return { key: cropKey, src: `${ART}/crop-${stage}-${cropKey}.webp`, ...GEOMETRY[`${stage}:${cropKey}`] };
}

/*
 * UI060-R1（用户第三张参考，c84a DECISION-GARDEN-REFERENCE-REWORK）：只有自家菜园明确 opt-in 时用，邻居、庭院、visual049 照旧走 cropArt。
 * 七张作物图来自 garden-reference-materials-001，原图在 public/ui-assets/UI060-r1/v1；UI2-GARDEN-8-02 起改用 I 重导的有损 webp
 * （UI-GARDEN-WEBP-V2B-20261013：作物用 512 最大边的 crops-512，种子袋图集原尺寸），只改路径。下面几何量自原图，512 版宽高比差不到 0.1%。
 * 每张是三株一丛，图只表达物种与阶段：白、粉萝卜“生长中”共用一张叶丛，两种豌豆共用植株，物种另由种子袋与名字区分；
 * 不拿成熟果代生长中。几何按 alpha≥128 的实心主体逐张量出（6c2b 10-08）：
 * - aspect：图宽 / 图高；
 * - cx：实心主体左右中心（0–1，相对图宽），对准土床中线；
 * - bottom：入土线（0–1，相对图高），落在土床的种植线上。番茄、豌豆、萝卜叶丛取实心主体底边——茎基坐在土面上，不漂浮；
 *   熟萝卜取球根中下部（白萝卜球根 0.60–0.83、最宽 0.71，入土线 0.76；粉萝卜球根 0.55–0.80，入土线 0.80），下半截由前景土层盖住，像长在土里；
 * - root：根茎作物（两种萝卜，两档都是），GardenBed 给它加前景土层；
 * - top：实心主体顶边，给成熟光晕、点按延伸用。
 * 表外作物用萝卜“生长中”那丛叶子作中性图，不冒充任何一种作物（和 cropArt 的 NEUTRAL 同一个做法）。
 */
const REF060R1 = "/ui-assets/UI-GARDEN-WEBP-V2B-20261013";
const REF060R1_CROPS = `${REF060R1}/crops-512`;

export interface ReferenceCropArt {
  key: KnownCrop | "unknown";
  src: string;
  aspect: number;
  cx: number;
  bottom: number;
  top: number;
  root: boolean;
}

type ReferenceGeometry = Omit<ReferenceCropArt, "key" | "src"> & { file: string };
const REF_TOMATO_GROWING: ReferenceGeometry = { file: "tomato-growing-v1.webp", aspect: 1254 / 1254, cx: 0.498, bottom: 0.8501, top: 0.1204, root: false };
const REF_TOMATO_RIPE: ReferenceGeometry = { file: "tomato-ripe-v1.webp", aspect: 1341 / 1173, cx: 0.5011, bottom: 0.8517, top: 0.1398, root: false };
const REF_RADISH_GROWING: ReferenceGeometry = { file: "radish-growing-v1.webp", aspect: 1227 / 1282, cx: 0.5037, bottom: 0.9087, top: 0.0694, root: true };
const REF_PEA_GROWING: ReferenceGeometry = { file: "pea-growing-v1.webp", aspect: 1246 / 1263, cx: 0.5008, bottom: 0.9279, top: 0.0578, root: false };
const REF_PEA_RIPE: ReferenceGeometry = { file: "pea-ripe-v1.webp", aspect: 1254 / 1254, cx: 0.502, bottom: 0.9346, top: 0.0542, root: false };

const REFERENCE_CROPS: Record<`${CropStage}:${KnownCrop}`, ReferenceGeometry> = {
  "growing:star_tomato": REF_TOMATO_GROWING,
  "ripe:star_tomato": REF_TOMATO_RIPE,
  "growing:moon_radish": REF_RADISH_GROWING,
  "ripe:moon_radish": { file: "radish-ripe-v1.webp", aspect: 1225 / 1284, cx: 0.5, bottom: 0.76, top: 0.0623, root: true },
  "growing:sakura_radish": REF_RADISH_GROWING,
  "ripe:sakura_radish": { file: "pink-radish-ripe-v1.webp", aspect: 1230 / 1278, cx: 0.5029, bottom: 0.8, top: 0.0759, root: true },
  "growing:sun_pea": REF_PEA_GROWING,
  "ripe:sun_pea": REF_PEA_RIPE,
  "growing:sea_salt_pea": REF_PEA_GROWING,
  "ripe:sea_salt_pea": REF_PEA_RIPE,
};

export function referenceCropArt(cropKey: string | null, stage: CropStage): ReferenceCropArt {
  const known = isKnownCrop(cropKey);
  const { file, ...geometry } = known ? REFERENCE_CROPS[`${stage}:${cropKey}`] : REF_RADISH_GROWING;
  // 表外作物借萝卜叶丛当中性图，但不当根茎作物（不加前景土层），和 cropArt 的 NEUTRAL 一致
  return { key: known ? cropKey : "unknown", src: `${REF060R1_CROPS}/${file}`, ...geometry, root: known && geometry.root };
}

/** Stage alone selects the footprint; no invented percentage or yield from artwork. */
export function referencePlantWidth(cropKey: string | null, stage: CropStage): number {
  if (!isKnownCrop(cropKey)) return 32;
  return stage === "growing" ? 36 : 46;
}

/**
 * 种子背包里的纸袋：一张 2172×724 透明图集（seed-packets-v1.webp），五格顺序 sun_pea、star_tomato、moon_radish、sea_salt_pea、sakura_radish，
 * 每格 viewBox 照材料包像素诊断 atlas_cells 原值。用代码里的 svg 视窗读一格，PNG 不裁不改；名字、数量另由 DOM 写，不靠图上的字。
 */
export const SEED_PACKET_ATLAS = { src: `${REF060R1}/seed-packets-v1.webp`, width: 2172, height: 724 } as const;
export const SEED_PACKET_CELLS: Record<KnownCrop, readonly [x: number, y: number, w: number, h: number]> = {
  sun_pea: [0, 0, 434, 724],
  star_tomato: [434, 0, 434, 724],
  moon_radish: [868, 0, 435, 724],
  sea_salt_pea: [1303, 0, 434, 724],
  sakura_radish: [1737, 0, 435, 724],
};

/** Visuals describe crop species only; growth and yield always come from the server. */
export function cropVisual(cropKey: string | null, stage: CropStage): string {
  return cropArt(cropKey, stage).src;
}

/** 收成小图（UI-ASSET-003 v1）：摘下来 / 收进篮子的那一颗。表外作物退回 cropArt 的中性图（和地里显示的一致）。 */
const PRODUCE = new Set(["star_tomato", "sun_pea", "sea_salt_pea", "moon_radish", "sakura_radish"]);
export function produceVisual(cropKey: string | null) {
  return cropKey && PRODUCE.has(cropKey) ? `/ui-assets/UI-ASSET-003/v1/produce-${cropKey}.webp` : cropArt(cropKey, "ripe").src;
}
